import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getApiUrlV2 } from '../utilities/url.utils';
import {
    CurrencyLanguageService,
    LocaleData,
} from '../shared/currency-language.service';
import { Observable, map, switchMap } from 'rxjs';
import {
    Filter,
    FilterDataDto,
    QuickFilter,
    transformFilterData,
} from './filter-data.model';

@Injectable({
    providedIn: 'root',
})
export class FilterDataService {
    constructor(
        private httpClient: HttpClient,
        private currencyLanguageService: CurrencyLanguageService
    ) {}

    getFilterData(sectionId: number): Observable<{ filters: Filter[] }> {
        return this.currencyLanguageService.getLocaleData().pipe(
            switchMap((localeData: LocaleData) => {
                let params = new HttpParams()
                    .set('cc', localeData.country)
                    .set('lang', localeData.language);
                if (!isNaN(sectionId)) {
                    params = params.set('sectionId', sectionId);
                }

                return this.httpClient.get<FilterDataDto>(
                    `${getApiUrlV2()}results/filters/widget`,
                    {
                        params,
                    }
                );
            }),
            map((filterData: FilterDataDto) => {
                return {
                    filters: transformFilterData(filterData),
                };
            })
        );
    }

    getQuickFilters(sectionId: number): Observable<QuickFilter[]> {
        return new Observable((observer) => {
            const quickFilters = this.quickFiltersBySectionId.get(sectionId);
            if (quickFilters) {
                observer.next(Array.from(quickFilters));
            }
            observer.complete();
        });
    }

    private quickFiltersBySectionId: Map<number, QuickFilter[]> = new Map<
        number,
        QuickFilter[]
    >([
        [
            3,
            [
                {
                    filterName: 'All-Inclusive',
                    filterNameFr: 'Tout compris',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 38,
                            filterName: 'All-Inclusive',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'All-Inclusive',
                                    filterId: 3560,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Beachfront',
                    filterNameFr: 'En bord de mer',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 27,
                            filterName: 'Beachfront',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Beachfront',
                                    filterId: 1900,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'North',
                    filterNameFr: 'Nord',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'North',
                                    filterId: 300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Adults only',
                    filterNameFr: 'Adultes seulement',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Property Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Adults only',
                                    filterId: 1740,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Luxury',
                    filterNameFr: 'Luxe',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Property Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Luxury',
                                    filterId: 1680,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Resort',
                    filterNameFr: 'Station',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Resort',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Resort',
                                    filterId: 620,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'South',
                    filterNameFr: 'Sud',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'South',
                                    filterId: 360,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Nature',
                    filterNameFr: 'Nature',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 27,
                            filterName: 'Setting',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Nature',
                                    filterId: 1920,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Private Stays',
                    filterNameFr: 'Séjours privés',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Private Stays',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Villas',
                                    filterId: 480,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Apartments',
                                    filterId: 520,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                                {
                                    label: 'Home',
                                    filterId: 660,
                                    orderIndex: 3,
                                    isSelected: false,
                                },
                                {
                                    label: 'Penthouse',
                                    filterId: 1700,
                                    orderIndex: 4,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: '4+ Stars',
                    filterNameFr: '4+ Étoiles',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 37,
                            filterName: 'Star Rating',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: '5 Stars',
                                    filterId: 3260,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: '4 Stars',
                                    filterId: 3280,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Self-Catering',
                    filterNameFr: 'Autonome',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 38,
                            filterName: 'Meal Plan',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Self-Catering',
                                    filterId: 3580,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Golf Course',
                    filterNameFr: 'Terrain de golf',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 27,
                            filterName: 'Golf Course',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Golf Course',
                                    filterId: 2040,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [
            8, // Day packages ------------------------------------
            [
                {
                    filterName: 'All-Inclusive',
                    filterNameFr: 'Tout compris',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 24,
                            filterName: 'All-Inclusive Package',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'All-Inclusive Package',
                                    filterId: 1340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Beachfront',
                    filterNameFr: 'En bord de mer',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 27,
                            filterName: 'Beachfront',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Beachfront',
                                    filterId: 1900,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Day Package',
                    filterNameFr: 'Forfait journée',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 24,
                            filterName: 'Package Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Day Package',
                                    filterId: 4580,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Evening Package',
                    filterNameFr: 'Forfait soirée',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 24,
                            filterName: 'Package Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Evening Package',
                                    filterId: 1300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Adults only',
                    filterNameFr: 'Adultes seulement',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Property Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Adults only',
                                    filterId: 1740,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Rooms & Meals',
                    filterNameFr: 'Chambres & Repas',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 24,
                            filterName: 'Package Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Rooms & Meals',
                                    filterId: 1320,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'North',
                    filterNameFr: 'Nord',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'North',
                                    filterId: 300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Luxury',
                    filterNameFr: 'Luxe',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Property Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Luxury',
                                    filterId: 1680,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Resort',
                    filterNameFr: 'Station',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 7,
                            filterName: 'Resort',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Resort',
                                    filterId: 620,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'South',
                    filterNameFr: 'Sud',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'South',
                                    filterId: 360,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Nature',
                    filterNameFr: 'Nature',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 27,
                            filterName: 'Setting',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Nature',
                                    filterId: 1920,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Golf Course',
                    filterNameFr: 'Terrain de golf',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 27,
                            filterName: 'Golf Course',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Golf Course',
                                    filterId: 2040,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [
            6,
            [
                {
                    filterName: 'North',
                    filterNameFr: 'Nord',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'North',
                                    filterId: 300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Beauty & Wellness',
                    filterNameFr: 'Beauté & Bien-être',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 28,
                            filterName: 'Beauty & Wellness',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Mani-Pedi',
                                    filterId: 2300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Manicure',
                                    filterId: 2940,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                                {
                                    label: 'Pedicure',
                                    filterId: 2960,
                                    orderIndex: 3,
                                    isSelected: false,
                                },
                                {
                                    label: 'Facial',
                                    filterId: 2980,
                                    orderIndex: 4,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hair Removal',
                                    filterId: 3040,
                                    orderIndex: 5,
                                    isSelected: false,
                                },
                                {
                                    label: 'Nail Varnish',
                                    filterId: 3020,
                                    orderIndex: 6,
                                    isSelected: false,
                                },
                                {
                                    label: 'Nail Extension',
                                    filterId: 3060,
                                    orderIndex: 7,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hair & Styling',
                                    filterId: 3140,
                                    orderIndex: 8,
                                    isSelected: false,
                                },
                                {
                                    label: 'Body Scrub',
                                    filterId: 3000,
                                    orderIndex: 9,
                                    isSelected: false,
                                },
                                {
                                    label: 'Body Wrap',
                                    filterId: 5460,
                                    orderIndex: 10,
                                    isSelected: false,
                                },
                                {
                                    label: 'Sauna',
                                    filterId: 3180,
                                    orderIndex: 11,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hamman',
                                    filterId: 5440,
                                    orderIndex: 12,
                                    isSelected: false,
                                },
                                {
                                    label: 'Eyelash Extension',
                                    filterId: 3080,
                                    orderIndex: 13,
                                    isSelected: false,
                                },
                                {
                                    label: 'Holistic Healing',
                                    filterId: 3160,
                                    orderIndex: 14,
                                    isSelected: false,
                                },
                                {
                                    label: 'Yoga & Fitness',
                                    filterId: 3220,
                                    orderIndex: 15,
                                    isSelected: false,
                                },
                                {
                                    label: 'Pool',
                                    filterId: 5480,
                                    orderIndex: 16,
                                    isSelected: false,
                                },
                                {
                                    label: 'Jacuzzi',
                                    filterId: 3200,
                                    orderIndex: 17,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Massages',
                    filterNameFr: 'Massages',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Couple',
                                    filterId: 1360,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Full body',
                                    filterId: 1420,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                                {
                                    label: 'Relaxing',
                                    filterId: 5300,
                                    orderIndex: 3,
                                    isSelected: false,
                                },
                                {
                                    label: 'Head & Shoulder',
                                    filterId: 5320,
                                    orderIndex: 4,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hot Stone',
                                    filterId: 1480,
                                    orderIndex: 5,
                                    isSelected: false,
                                },
                                {
                                    label: 'Thai',
                                    filterId: 1540,
                                    orderIndex: 6,
                                    isSelected: false,
                                },
                                {
                                    label: 'Aromatherapy',
                                    filterId: 1580,
                                    orderIndex: 7,
                                    isSelected: false,
                                },
                                {
                                    label: 'Deep Tissue',
                                    filterId: 1400,
                                    orderIndex: 8,
                                    isSelected: false,
                                },
                                {
                                    label: 'Foot',
                                    filterId: 1460,
                                    orderIndex: 9,
                                    isSelected: false,
                                },
                                {
                                    label: 'Back & Neck',
                                    filterId: 5340,
                                    orderIndex: 10,
                                    isSelected: false,
                                },
                                {
                                    label: 'Swedish',
                                    filterId: 1380,
                                    orderIndex: 12,
                                    isSelected: false,
                                },
                                {
                                    label: 'Balinese',
                                    filterId: 5400,
                                    orderIndex: 14,
                                    isSelected: false,
                                },
                                {
                                    label: 'Ayurvedic',
                                    filterId: 1560,
                                    orderIndex: 16,
                                    isSelected: false,
                                },
                                {
                                    label: 'Prenatal',
                                    filterId: 1520,
                                    orderIndex: 17,
                                    isSelected: false,
                                },
                                {
                                    label: 'Aromatherapy',
                                    filterId: 5380,
                                    orderIndex: 19,
                                    isSelected: false,
                                },
                                {
                                    label: 'Foot',
                                    filterId: 1460,
                                    orderIndex: 20,
                                    isSelected: false,
                                },
                                {
                                    label: 'Reflexology',
                                    filterId: 1440,
                                    orderIndex: 21,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hot Oil',
                                    filterId: 3740,
                                    orderIndex: 22,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hair Spa',
                                    filterId: 5420,
                                    orderIndex: 23,
                                    isSelected: false,
                                },
                                {
                                    label: 'Cupping',
                                    filterId: 3680,
                                    orderIndex: 24,
                                    isSelected: false,
                                },
                                {
                                    label: 'Slimming Session',
                                    filterId: 3700,
                                    orderIndex: 25,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Couples',
                    filterNameFr: 'Soins en couple',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Couple',
                                    filterId: 1360,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Full body',
                    filterNameFr: 'Corps entier',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Full body',
                                    filterId: 1420,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Relaxing',
                    filterNameFr: 'Relaxant',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Relaxing',
                                    filterId: 5300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Head & Shoulder',
                    filterNameFr: 'Tête & Épaules',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Head & Shoulder',
                                    filterId: 5320,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Back & Neck',
                    filterNameFr: 'Dos & Cou',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Back & Neck',
                                    filterId: 5340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Body Scrub',
                    filterNameFr: 'Gommage corporel',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 28,
                            filterName: 'Beauty & Wellness',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Body Scrub',
                                    filterId: 3000,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Mani-Pedi',
                    filterNameFr: 'Mani-Pédi',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 28,
                            filterName: 'Beauty & Wellness',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Mani-Pedi',
                                    filterId: 2300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Manicure',
                    filterNameFr: 'Manucure',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 28,
                            filterName: 'Beauty & Wellness',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Manicure',
                                    filterId: 2940,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Deep tissue',
                    filterNameFr: 'Tissu profond',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Deep tissue',
                                    filterId: 1400,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Reflexology',
                    filterNameFr: 'Réflexologie',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 25,
                            filterName: 'Massages',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Reflexology',
                                    filterId: 1440,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Hair Removal',
                    filterNameFr: 'Épilation',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 28,
                            filterName: 'Beauty & Wellness',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Hair Removal',
                                    filterId: 3040,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: '4+ Stars',
                    filterNameFr: '4+ Étoiles',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 37,
                            filterName: 'Star Rating',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: '5 Stars',
                                    filterId: 3260,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: '4 Stars',
                                    filterId: 3280,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },

                {
                    filterName: 'Nails',
                    filterNameFr: '',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 28,
                            filterName: 'Beauty & Wellness',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Mani-Pedi',
                                    filterId: 2300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Manicure',
                                    filterId: 2940,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                                {
                                    label: 'Nail Varnish',
                                    filterId: 3020,
                                    orderIndex: 3,
                                    isSelected: false,
                                },
                                {
                                    label: 'Nail Extension',
                                    filterId: 3060,
                                    orderIndex: 4,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [
            4,
            [
                {
                    filterName: 'North',
                    filterNameFr: 'Nord',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'North',
                                    filterId: 300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Beachfront',
                    filterNameFr: 'En bord de mer',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 31,
                            filterName: 'Settings',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Beachfront',
                                    filterId: 5180,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'All-Inclusive',
                    filterNameFr: 'Tout compris',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'All-Inclusive',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'All-Inclusive',
                                    filterId: 4720,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Buffet',
                    filterNameFr: 'Buffet',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Buffet',
                                    filterId: 4680,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'A La Carte',
                    filterNameFr: 'A La Carte',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: '"A La Carte',
                                    filterId: 4700,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'East',
                    filterNameFr: 'Est',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'East',
                                    filterId: 320,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Luxury Dining',
                    filterNameFr: 'Restauration de luxe',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 31,
                            filterName: 'Settings',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Luxury Dining',
                                    filterId: 2520,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Romantic',
                    filterNameFr: 'Romantique',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 31,
                            filterName: 'Settings',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Romantic',
                                    filterId: 2540,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Lunch',
                    filterNameFr: 'Déjeuner',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Lunch',
                                    filterId: 4780,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Dinner',
                    filterNameFr: 'Dîner',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Dinner',
                                    filterId: 4760,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },

                {
                    filterName: 'Lunch Buffet',
                    filterNameFr: 'Déjeuner Buffet',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Lunch',
                                    filterId: 4780,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Buffet',
                                    filterId: 4680,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Dinner Buffet',
                    filterNameFr: 'Dîner Buffet',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 43,
                            filterName: 'Meal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Dinner',
                                    filterId: 4760,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Buffet',
                                    filterId: 4680,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [
            7,
            [
                {
                    filterName: 'Catamaran',
                    filterNameFr: 'Catamaran',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 40,
                            filterName: 'Water Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Catamaran',
                                    filterId: 3840,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Quad & Buggy',
                    filterNameFr: 'Quad & Buggy',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 42,
                            filterName: 'Land Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Quad & Buggy',
                                    filterId: 4380,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'North',
                    filterNameFr: 'Nord',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'North',
                                    filterId: 300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Nature Parks',
                    filterNameFr: 'Parcs naturels',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 42,
                            filterName: 'Land Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Nature Parks',
                                    filterId: 4400,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Full-Day Tours',
                    filterNameFr: "Excursions d'une journée",
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 40,
                            filterName: 'Water Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Full-Day Tours',
                                    filterId: 5580,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Half-Day Tours',
                    filterNameFr: "Excursions d'une journée",
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 40,
                            filterName: 'Water Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Half-Day Tours',
                                    filterId: 5600,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Whales & Dolphins',
                    filterNameFr: 'Baleines & Dauphins',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 40,
                            filterName: 'Water Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Whales & Dolphins',
                                    filterId: 4640,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Horse Riding',
                    filterNameFr: 'Équitation',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 42,
                            filterName: 'Land Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Horse Riding',
                                    filterId: 4460,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Water Activities',
                    filterNameFr: 'Activités nautiques',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 40,
                            filterName: 'Water Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Catamaran',
                                    filterId: 3840,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Speed Boat',
                                    filterId: 3860,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                                {
                                    label: 'Underwater',
                                    filterId: 5680,
                                    orderIndex: 3,
                                    isSelected: false,
                                },
                                {
                                    label: 'Whales & Dolphins',
                                    filterId: 4640,
                                    orderIndex: 4,
                                    isSelected: false,
                                },
                                {
                                    label: 'Snorkeling',
                                    filterId: 4000,
                                    orderIndex: 5,
                                    isSelected: false,
                                },
                                {
                                    label: 'Scuba Diving',
                                    filterId: 3920,
                                    orderIndex: 6,
                                    isSelected: false,
                                },
                                {
                                    label: 'Kayaking',
                                    filterId: 3980,
                                    orderIndex: 7,
                                    isSelected: false,
                                },
                                {
                                    label: 'Parasailing',
                                    filterId: 3940,
                                    orderIndex: 8,
                                    isSelected: false,
                                },
                                {
                                    label: 'Tube Ride',
                                    filterId: 3960,
                                    orderIndex: 9,
                                    isSelected: false,
                                },
                                {
                                    label: 'Undersea Walk',
                                    filterId: 4100,
                                    orderIndex: 10,
                                    isSelected: false,
                                },
                                {
                                    label: 'Kitesurfing',
                                    filterId: 4040,
                                    orderIndex: 11,
                                    isSelected: false,
                                },
                                {
                                    label: 'Windsurfing',
                                    filterId: 4080,
                                    orderIndex: 12,
                                    isSelected: false,
                                },
                                {
                                    label: 'Stand-up Paddle',
                                    filterId: 4020,
                                    orderIndex: 13,
                                    isSelected: false,
                                },
                                {
                                    label: 'Water Skiing',
                                    filterId: 4120,
                                    orderIndex: 14,
                                    isSelected: false,
                                },
                                {
                                    label: 'Big Game Fishing',
                                    filterId: 3900,
                                    orderIndex: 15,
                                    isSelected: false,
                                },
                                {
                                    label: 'Speed Boat',
                                    filterId: 3860,
                                    orderIndex: 16,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Land Activities',
                    filterNameFr: 'Activités terrestres',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 42,
                            filterName: 'Land Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Quad & Buggy',
                                    filterId: 4380,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Nature Parks',
                                    filterId: 4400,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                                {
                                    label: 'Golf',
                                    filterId: 4420,
                                    orderIndex: 3,
                                    isSelected: false,
                                },
                                {
                                    label: 'Hiking & Trails',
                                    filterId: 4440,
                                    orderIndex: 4,
                                    isSelected: false,
                                },
                                {
                                    label: 'Horse Riding',
                                    filterId: 4460,
                                    orderIndex: 5,
                                    isSelected: false,
                                },
                                {
                                    label: 'Zipline',
                                    filterId: 4480,
                                    orderIndex: 6,
                                    isSelected: false,
                                },
                                {
                                    label: 'Biking',
                                    filterId: 4500,
                                    orderIndex: 7,
                                    isSelected: false,
                                },
                                {
                                    label: 'Archery',
                                    filterId: 4520,
                                    orderIndex: 8,
                                    isSelected: false,
                                },
                                {
                                    label: 'Gym & Fitness',
                                    filterId: 4540,
                                    orderIndex: 9,
                                    isSelected: false,
                                },
                                {
                                    label: 'Aquarium',
                                    filterId: 5200,
                                    orderIndex: 10,
                                    isSelected: false,
                                },
                                {
                                    label: 'Rental',
                                    filterId: 5220,
                                    orderIndex: 11,
                                    isSelected: false,
                                },
                                {
                                    label: 'Classes & Workshops',
                                    filterId: 5240,
                                    orderIndex: 12,
                                    isSelected: false,
                                },
                                {
                                    label: "Kid's Activities",
                                    filterId: 5260,
                                    orderIndex: 13,
                                    isSelected: false,
                                },
                                {
                                    label: 'Discoveries',
                                    filterId: 5280,
                                    orderIndex: 14,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Helicopter',
                    filterNameFr: 'Hélicoptère',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 41,
                            filterName: 'Air Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Helicopter',
                                    filterId: 4160,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Seaplane',
                                    filterId: 4600,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: 'Skydiving',
                                    filterId: 4140,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Helicopter',
                    filterNameFr: 'Hélicoptère',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 41,
                            filterName: 'Air Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Helicopter',
                                    filterId: 4160,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Speed Boat',
                    filterNameFr: 'Bateau rapide',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 40,
                            filterName: 'Water Activities',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Speed Boat',
                                    filterId: 3860,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'South',
                    filterNameFr: 'Sud',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'South',
                                    filterId: 360,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [
            9,
            [
                {
                    filterName: 'Hotels',
                    filterNameFr: 'Hôtels',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 36,
                            filterName: 'Deal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Hotels',
                                    filterId: 2820,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Excursions',
                    filterNameFr: 'Excursions',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 36,
                            filterName: 'Deal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Excursions',
                                    filterId: 2840,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Car Rentals',
                    filterNameFr: 'Location de voitures',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 36,
                            filterName: 'Deal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Car Rentals',
                                    filterId: 2860,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Airport Transfer',
                    filterNameFr: 'Transfert aéroport',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 36,
                            filterName: 'Deal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Airport Transfer',
                                    filterId: 6140,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'North',
                    filterNameFr: 'Nord',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'North',
                                    filterId: 300,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'East',
                    filterNameFr: 'Est',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'Location',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'East',
                                    filterId: 320,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'West',
                    filterNameFr: 'Ouest',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 2,
                            filterName: 'West',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'West',
                                    filterId: 340,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [19, []],
        [
            18,
            [
                {
                    filterName: '4+ Stars',
                    filterNameFr: '4+ Étoiles',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 37,
                            filterName: 'Star Rating',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: '4 Stars',
                                    filterId: 3280,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                                {
                                    label: '5 Stars',
                                    filterId: 3260,
                                    orderIndex: 2,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Airport Transfer',
                    filterNameFr: 'Transfert aéroport',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 36,
                            filterName: 'Deal Type',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Airport Transfer',
                                    filterId: 6140,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
        [
            undefined,
            [
                {
                    filterName: 'Flash Sales',
                    filterNameFr: 'Ventes Flash',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 45,
                            filterName: 'Popular Filters',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Flash Sales',
                                    filterId: 5560,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Month Special',
                    filterNameFr: 'Offre du mois',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 45,
                            filterName: 'Popular Filters',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Flash Sales',
                                    filterId: 5500,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Spa Break',
                    filterNameFr: 'Pause Spa',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 45,
                            filterName: 'Popular Filters',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Spa Break',
                                    filterId: 5540,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'Bundle Offers',
                    filterNameFr: 'Offres groupées',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 45,
                            filterName: 'Popular Filters',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'Bundle Offers',
                                    filterId: 5520,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
                {
                    filterName: 'School Holiday',
                    filterNameFr: 'Vacances scolaires',
                    hasLinkedDeals: true,
                    filtersToApply: [
                        {
                            filterTypeId: 3,
                            filterName: 'Popular Filters',
                            isSelected: false,
                            filterValues: [
                                {
                                    label: 'School Holiday',
                                    filterId: 400,
                                    orderIndex: 1,
                                    isSelected: false,
                                },
                            ],
                        },
                    ],
                    isSelected: false,
                },
            ],
        ],
    ]);
}
